/**
 * BANFF-AREA — roster wave entries. GENERATED BY ASSEMBLY, WRITTEN BY REVIEW.
 *
 * Geography (streets, landmarks, coordinates, adjacency) ported from the source
 * roster; airspace derived by the calibrated classifier in
 * scripts/derive-airspace.mjs; every descriptive sentence written fresh from
 * aerial-media truth and fact-checked against its generation brief. See
 * docs/geo/VISION-v3.md for the fact contract these entries were written under.
 *
 * 3 entries. Edit by hand freely — assembly never overwrites a
 * file that has drifted from its fragments without --force.
 */
import type { Location } from "../types";

export const ROSTER_BANFF_AREA: Location[] = [
// ─────────────────────────────────────────────────────── CASTLE JUNCTION ──
  {
    slug: "castle-junction",
    name: "Castle Junction",
    region: "banff-area",
    city: "Improvement District No. 9",
    province: "Alberta",
    coordinates: { lat: 51.2778, lng: -115.9219 },
    tier: 4,
    driveMinutesFromCalgary: 110,
    airspace: {
      classification: "Banff National Park — Parks Canada permit regime",
      nearestAerodrome: "None relevant — park law governs before airspace class",
      distanceToAerodromeKm: 0,
      authorizationRequired: true,
      authorizationNote:
        "Castle Junction lies inside Banff National Park. Parks Canada prohibits launching, landing and operating a drone within the park without a permit, and those permits are issued for restricted purposes that do not ordinarily include commercial listing or marketing work. What can be shot legally is shot from outside the park boundary.",
      source: "parks-canada",
      lastVerified: "2026-09-01",
      restrictedZones: ["Banff National Park (entire area)"],
    },
    flightStatus: "permit-required",
    airspaceFact: {
      hook: "Castle Junction's airspace record carries an unusual line: no relevant aerodrome. Where Highway 93 South meets the Trans-Canada, the distance that decides a flight is not the distance to a runway — it is the distance to the boundary of Banff National Park, and the junction sits well inside it whichever way you measure.",
      consequence:
        "That geometry removes the usual workarounds. There is no near edge to stage from, no adjacent uncontrolled pocket, and no certificate that applies — a shoot at Castle Mountain Chalets or Storm Mountain Lodge cannot be flown as a routine commercial job at all.",
      resolution:
        "Fly4MEdia states that position before any quote is issued. What is legal is shot from outside the park boundary, lawful vantages down-valley are proposed in its place, and nothing is priced that cannot lawfully be flown.",
    },
    landmarks: [
      "Bow Valley Parkway",
      "Castle Mountain",
      "Castle Mountain Chalets",
      "Storm Mountain Lodge",
      "Highway 93 South",
    ],
    terrain:
      "A valley-floor crossroads where Highway 93 South leaves the Trans-Canada, with the cliff bands of Castle Mountain rising straight off the forest on the north side of the junction.",
    windNote:
      "The valley the Bow Valley Parkway follows funnels wind along its axis, and chinook westerlies run the corridor's full length — academic for aerial work here, since the binding constraint is legal rather than meteorological.",
    seasonalWindow:
      "Largely academic for aerial work. Snow holds late on the high ground around Castle Mountain and the valley floor greens first, but the limit on flying here is the park boundary, not the season.",
    bylawNote:
      "Parks Canada's national-park regulations govern everything at the junction; there is no municipal layer beneath them and no exception created by lodge or leasehold land.",
    subjectMix: ["resort-terrain", "tourism-hospitality"],
    mobilisationNote:
      "About 110 minutes from the Calgary base, the last stretch of it deep inside the park — and it is the boundary, not the distance, that decides what can be flown.",
    faqs: [
      {
        question: "Can you fly a drone at Castle Junction?",
        answer:
          "No. Castle Junction sits inside Banff National Park, and Parks Canada prohibits launching, landing or operating a drone anywhere in the park without a permit. Permits exist, but for restricted purposes — science, park management — not routine commercial marketing. Anything legal is photographed from beyond the park boundary, and we say so before quoting.",
      },
      {
        question:
          "Can Castle Mountain Chalets or Storm Mountain Lodge commission drone footage of their properties?",
        answer:
          "Not lawfully by drone from inside the park. Both properties sit within Banff National Park, so the prohibition applies to their own grounds as much as to the peaks around them — owning or leasing the land does not create a launch right. Marketing imagery for lodges here is built from what can be shot legally.",
      },
      {
        question: "Is any part of Castle Junction outside the park?",
        answer:
          "No. Every road that meets at the junction — the Trans-Canada, Highway 93 South, the Bow Valley Parkway — runs inside Banff National Park at that point, and so does everything visible from it. There is no near edge to work from; the lawful vantages sit well down-valley, outside the boundary, and are identified before booking.",
      },
    ],
    primaryKeywords: [
      "drone photography Castle Junction",
      "aerial photography Castle Junction",
      "Castle Junction drone services",
    ],
    nearestLocations: ["banff", "lake-louise", "tunnel-mountain"],
  },

  // ──────────────────────────────────────────────────────── MIDDLE SPRINGS ──
  {
    slug: "middle-springs",
    name: "Middle Springs",
    region: "banff-area",
    city: "Banff",
    province: "Alberta",
    coordinates: { lat: 51.1735, lng: -115.5825 },
    tier: 4,
    driveMinutesFromCalgary: 90,
    airspace: {
      classification: "Banff National Park — Parks Canada permit regime",
      nearestAerodrome: "None relevant — park law governs before airspace class",
      distanceToAerodromeKm: 0,
      authorizationRequired: true,
      authorizationNote:
        "Middle Springs lies inside Banff National Park. Parks Canada prohibits launching, landing and operating a drone within the park without a permit, and those permits are issued for restricted purposes that do not ordinarily include commercial listing or marketing work. What can be shot legally is shot from outside the park boundary.",
      source: "parks-canada",
      lastVerified: "2026-09-01",
      restrictedZones: ["Banff National Park (entire area)"],
    },
    flightStatus: "permit-required",
    airspaceFact: {
      hook: "Middle Springs climbs the treed slope behind the Cave and Basin National Historic Site, on the shaded side of the valley — evening light leaves Cave Ave and Grizzly St while the far wall of the valley is still bright.",
      consequence:
        "In most towns that aspect would simply push a shoot to morning. Here the scheduling question never arrives: the neighbourhood lies inside Banff National Park, and no routine commercial drone flight can be planned over it in any light.",
      resolution:
        "The honest sequence is legal position first, light second. Fly4MEdia says plainly what cannot be flown, and anything that can be — from outside the park boundary — is planned against the valley's real light and delivered in the standard turnaround.",
    },
    landmarks: [
      "Cave and Basin National Historic Site",
      "Cave Ave",
      "Grizzly St",
      "Sundance Trail",
      "Middle Springs Park",
    ],
    terrain:
      "A bench of curving residential streets cut into forested mountainside above the Cave and Basin National Historic Site, with the ground climbing steeply behind the lots and falling to the valley floor in front of them.",
    windNote:
      "Chinook-belt westerlies cross the whole region, with sharp differences between the sheltered lower streets and the exposed slope above — academic for flight in the neighbourhood, where park law settles the question first.",
    seasonalWindow:
      "The shaded aspect keeps snow on the slope late and takes evening sun off the streets early — real characteristics of the place, and academic for drone work, since park law closes the question before the light does.",
    bylawNote:
      "National-park regulation applies throughout the neighbourhood. Town of Banff rules operate beneath Parks Canada's and do not create any drone exception on residential streets.",
    subjectMix: ["luxury-residential", "tourism-hospitality"],
    mobilisationNote:
      "Roughly 90 minutes from the Calgary base. Mobilisation is trivial; the park boundary is the constraint that matters.",
    faqs: [
      {
        question: "Can a drone be flown over Middle Springs?",
        answer:
          "No. Middle Springs is a Banff townsite neighbourhood, and the whole townsite lies inside Banff National Park. Parks Canada's prohibition on launching, landing and operating drones applies on residential streets exactly as it does in the backcountry, and the permits that exist cover restricted purposes, not property marketing. The legal position is stated before any quote.",
      },
      {
        question: "Can a Middle Springs listing include aerial photography?",
        answer:
          "Not drone photography flown over the property — no operator can lawfully offer that inside the park, whatever their certification. A listing on Grizzly St or Cave Ave is sold on ground-level and legally sourced imagery instead, and any quote promising a routine drone shoot over a Banff home should be treated as a warning sign.",
      },
    ],
    primaryKeywords: [
      "drone photography Middle Springs",
      "aerial photography Middle Springs",
      "Middle Springs drone services",
    ],
    nearestLocations: ["banff", "tunnel-mountain", "castle-junction"],
  },

  // ─────────────────────────────────────────────────────── TUNNEL MOUNTAIN ──
  {
    slug: "tunnel-mountain",
    name: "Tunnel Mountain",
    region: "banff-area",
    city: "Banff",
    province: "Alberta",
    coordinates: { lat: 51.1793, lng: -115.5497 },
    tier: 4,
    driveMinutesFromCalgary: 90,
    airspace: {
      classification: "Banff National Park — Parks Canada permit regime",
      nearestAerodrome: "None relevant — park law governs before airspace class",
      distanceToAerodromeKm: 0,
      authorizationRequired: true,
      authorizationNote:
        "Tunnel Mountain lies inside Banff National Park. Parks Canada prohibits launching, landing and operating a drone within the park without a permit, and those permits are issued for restricted purposes that do not ordinarily include commercial listing or marketing work. What can be shot legally is shot from outside the park boundary.",
      source: "parks-canada",
      lastVerified: "2026-09-01",
      restrictedZones: ["Banff National Park (entire area)"],
    },
    flightStatus: "permit-required",
    airspaceFact: {
      hook: "The water below Tunnel Mountain is seen almost entirely from above: Surprise Corner and the Hoodoos Viewpoint both stand where the ground falls away to a river bend, which is precisely the geometry an aerial camera exists to exploit.",
      consequence:
        "That composition cannot be commissioned from a drone. The viewpoints, the resort roads and the river below them all lie inside Banff National Park, and the Parks Canada prohibition covers the whole frame — there is no legal launch point anywhere in it.",
      resolution:
        "Fly4MEdia says so before quoting. The elevated river angle is reached lawfully on foot at the viewpoints, whatever is legal by air is shot from outside the park boundary, and no flight is promised that park law forbids.",
    },
    landmarks: [
      "Surprise Corner",
      "Hoodoos Viewpoint",
      "Tunnel Mountain Trail",
      "Banff Rocky Mountain Resort",
      "Tunnel Mountain Dr",
    ],
    terrain:
      "A forested shoulder on the east side of the townsite, benched with resort and campground roads, dropping past hoodoo spires to a river bend the viewpoints were built to overlook.",
    windNote:
      "Chinook winds work through the mountain corridor with strong local variation around the shoulder of Tunnel Mountain itself; for aerial work the note is academic, because the constraint here is legal, not weather.",
    seasonalWindow:
      "The season changes what the viewpoints see — open water against snow in winter, full autumn colour at the river bend — and none of it changes the legal position, which is the actual constraint on aerial work.",
    bylawNote:
      "Parks Canada regulation covers the entire shoulder, including the resort leaseholds and the viewpoints; no property-level consent overrides a national-park prohibition.",
    subjectMix: ["tourism-hospitality", "resort-terrain", "luxury-residential"],
    mobilisationNote:
      "About 90 minutes from the Calgary base. The drive has never been the issue here — the legal position is.",
    faqs: [
      {
        question: "What are the drone rules at Tunnel Mountain?",
        answer:
          "The same rule that covers the whole townsite: Tunnel Mountain lies inside Banff National Park, and Parks Canada prohibits drone launch, landing and operation without a permit. The prohibition covers the resort roads, the trail, the viewpoints and the river below them alike. Permits are issued for restricted purposes and do not ordinarily include marketing work.",
      },
      {
        question: "Can Banff Rocky Mountain Resort get drone footage of its grounds?",
        answer:
          "No — the resort sits inside the park, and the prohibition applies on its own grounds regardless of consent or certification. What the property can use lawfully is imagery made from outside the park boundary or from the ground, and an honest quote says exactly that rather than pricing a flight that cannot happen.",
      },
      {
        question: "Why do Surprise Corner and the Hoodoos Viewpoint matter if drones are banned?",
        answer:
          "Because they already provide the elevated angle lawfully. Both were built where the ground itself gives the raised view a drone would otherwise chase — the river bend, the spires, the valley wall. A camera on a tripod at either viewpoint is legal; a drone above them is not, and that trade is worth knowing before commissioning.",
      },
    ],
    primaryKeywords: [
      "drone photography Tunnel Mountain",
      "aerial photography Tunnel Mountain",
      "Tunnel Mountain drone services",
    ],
    nearestLocations: ["banff", "middle-springs", "castle-junction"],
  }
];
